<template>
  <div class="base-textarea">
    <textarea
      v-model="model"
      :name="props.name"
      :maxlength="props.maxlength"
      class="base-textarea__control"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :class="computedClass"
    />
    <span
      v-if="hasClear"
      class="base-textarea__clear"
    >
      <BaseIcon
        name="vuesax:close-circle-linear"
        color="var(--color-foreground-caption)"
        size="20px"
        clickable
        @click="handleClear"
      />
    </span>
    <div v-if="props.maxlength" class="base-textarea__counter">
      {{ model.length }} / {{ props.maxlength }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAnimationShake } from '../../lib'
import BaseIcon from '../icon/index.vue'

interface IProps {
  placeholder?: string | undefined
  name?: string
  variant?: 'secondary' | 'light'
  size?: 'md'
  height?: string
  maxlength?: number
  disabled?: boolean
  readonly?: boolean
  withClear?: boolean
  alwaysShowClear?: boolean
  errorMessage?: string
}

const props = withDefaults(defineProps<IProps>(), {
  variant: 'secondary',
  size: 'md',
  height: 'auto',
})
const emit = defineEmits<{
  (e: 'cleared', fieldName?: string): void
}>()
const model = defineModel<string>({ required: true })

const { shakeClass } = useAnimationShake(() => props.errorMessage)

const computedClass = computed(() => ({
  [`base-textarea--variant-${props.variant}`]: true,
  [`base-textarea--size-${props.size}`]: true,
  [shakeClass.value]: true,
  'base-textarea--error': props.errorMessage,
}))
const hasClear = computed(() => props.withClear && (props.alwaysShowClear || model))

function handleClear() {
  model.value = ''
  emit('cleared', props.name)
}
</script>

<style scoped lang="scss">
.base-textarea {
  width: 100%;
  position: relative;
  height: calc(v-bind(height) - 4px);

  &__control {
    @include drop-button-styles;

    border-radius: var(--radius-xs);
    height: 100%;
    width: 100%;
    padding: rem(12px);

    &::placeholder {
      color: var(--color-foreground-caption);
    }
  }

  &__counter {
    position: absolute;
    bottom: rem(12px);
    right: rem(12px);
    color: var(--color-foreground-caption);
    font-size: rem(12px);
    line-height: rem(14px);
    font-weight: 600;
  }

  &__clear {
    position: absolute;
    top: rem(9px);
    right: rem(9px);
  }

  &--variant {
    &-light {
      background-color: var(--color-surface-bg);
      border: 2px solid var(--color-surface-bg);
    }

    &-secondary {
      background-color: var(--color-surface-placeholder);
      border: 2px solid var(--color-surface-placeholder);
    }
  }

  &--size {
    &-md {
      font-size: rem(14px);
      line-height: rem(14px);
      font-weight: 400;
    }
  }

  &--error {
    border: 2px solid var(--color-surface-danger);
  }
}
</style>
